/* =========================
   PEGUU — ORDER SUMMARY
========================= */

(() => {
  "use strict";


  /* =========================
     CONFIGURACIÓN
  ========================== */

  const ORDER_UPDATED_EVENT =
    "peguu:order-updated";

  const QR_CONFIG_UPDATED_EVENT =
    "peguu:qr-config-updated";

  const QR_CONFIG_STORAGE_KEY =
    "peguu:qr-config";

  const SELECTED_MATERIAL_STORAGE_KEY =
    "peguu:selected-material";

  const QR_PRODUCT_CODE =
    "qr_sticker";

  const DESIGN_FILE_DB =
    "peguu-design-files";

  const DESIGN_FILE_STORE =
    "files";

  const localPreviewUrls =
    new WeakMap();

  const imageRenderVersions =
    new WeakMap();


  /* =========================
     FORMATEADORES
  ========================== */

  const currencyFormatter =
    new Intl.NumberFormat(
      "es-MX",
      {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );


  const quantityFormatter =
    new Intl.NumberFormat(
      "es-MX",
      {
        maximumFractionDigits: 0
      }
    );


  /* =========================
     NORMALIZAR TEXTO
  ========================== */

  const normalizeText = (
    value
  ) => {
    return String(
      value || ""
    )
      .normalize(
        "NFD"
      )
      .replace(
        /[\u0300-\u036f]/g,
        ""
      )
      .trim()
      .toLowerCase();
  };


  /* =========================
     LEER SESSION STORAGE
  ========================== */

  const getStoredJson = (
    key
  ) => {
    try {

      const storedValue =
        sessionStorage.getItem(
          key
        );


      if (
        !storedValue
      ) {
        return null;
      }


      const parsedValue =
        JSON.parse(
          storedValue
        );


      if (
        !parsedValue ||
        typeof parsedValue !==
          "object"
      ) {
        return null;
      }


      return parsedValue;


    } catch (
      error
    ) {

      console.warn(
        `Peguu Order Summary: no fue posible leer ${key}.`,
        error
      );


      return null;
    }
  };


  /* =========================
     OBTENER CONFIGURACIÓN QR
  ========================== */

  const getQrConfig =
    () => {

      const storedQrConfig =
        getStoredJson(
          QR_CONFIG_STORAGE_KEY
        );


      if (
        storedQrConfig
      ) {
        return storedQrConfig;
      }


      /*
       * Compatibilidad por si existe
       * un controlador QR en window.
       */

      if (
        window.PeguuQrConfig &&
        typeof window.PeguuQrConfig
          .getQrConfig ===
          "function"
      ) {

        try {

          return (
            window.PeguuQrConfig
              .getQrConfig() ||
            null
          );


        } catch (
          error
        ) {

          console.warn(
            "Peguu Order Summary: no fue posible obtener la configuración QR.",
            error
          );
        }
      }


      return null;
    };


  /* =========================
     OBTENER MATERIAL
     SELECCIONADO
  ========================== */

  const getSelectedMaterial =
    () => {
      return getStoredJson(
        SELECTED_MATERIAL_STORAGE_KEY
      );
    };


  /* =========================
     OBTENER PEDIDO
  ========================== */

  const getOrder =
    () => {
      if (
        !window.PeguuOrder ||
        typeof window.PeguuOrder.get !==
          "function"
      ) {
        return null;
      }


      try {

        return (
          window.PeguuOrder.get() ||
          null
        );


      } catch (
        error
      ) {

        console.warn(
          "Peguu Order Summary: no fue posible obtener el pedido.",
          error
        );


        return null;
      }
    };


  /* =========================
     ESPERAR CARRITO GLOBAL
  ========================== */

  const waitForPeguuCart = (
    timeoutMs = 5000
  ) => {
    if (
      typeof window.PeguuCart
        ?.getCurrentCart ===
        "function"
    ) {
      return Promise.resolve(
        window.PeguuCart
      );
    }

    return new Promise((resolve, reject) => {
      let timeoutId = null;

      const cleanup = () => {
        document.removeEventListener(
          "peguu:cart:ready",
          handleReady
        );

        if (timeoutId) {
          window.clearTimeout(
            timeoutId
          );
        }
      };

      const handleReady = () => {
        if (
          typeof window.PeguuCart
            ?.getCurrentCart !==
            "function"
        ) {
          return;
        }

        cleanup();
        resolve(window.PeguuCart);
      };

      document.addEventListener(
        "peguu:cart:ready",
        handleReady
      );

      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(
          new Error(
            "PeguuCart no está disponible."
          )
        );
      }, timeoutMs);

      handleReady();
    });
  };


  /* =========================
     ADAPTAR ARTÍCULO ÚNICO
     AL RESUMEN INDIVIDUAL
  ========================== */

  const adaptCartItemToOrder = (
    item
  ) => {
    if (!item) {
      return null;
    }

    return {
      material: {
        id:
          item.materialId ||
          null,

        name:
          item.materialName ||
          "",

        imageUrl:
          item.materialImageUrl ||
          "",

        productCode:
          item.productCode ||
          ""
      },

      quote: {
        quantity:
          item.quantity,

        unitPrice:
          item.unitPrice,

        subtotal:
          item.lineTotal ??
          item.subtotal,

        finalTotal:
          item.lineTotal ??
          item.subtotal
      },

      design:
        item.design ||
        null,

      qrConfig:
        item.qrConfig ||
        item.qr ||
        null
    };
  };


  /* =========================
     FALLBACK DEL PAGO
     CON UN SOLO PRODUCTO
  ========================== */

  const getSingleCartOrder = async (
    summary
  ) => {
    if (
      summary?.dataset
        .orderSummaryCartFallback !==
        "true"
    ) {
      return null;
    }

    try {
      const peguuCart =
        await waitForPeguuCart();

      const cart =
        await peguuCart
          .getCurrentCart();

      const items =
        Array.isArray(
          cart?.items
        )
          ? cart.items
          : [];

      if (
        items.length !== 1
      ) {
        return null;
      }

      return adaptCartItemToOrder(
        items[0]
      );
    } catch (error) {
      console.warn(
        "Peguu Order Summary: no fue posible recuperar el producto único del carrito.",
        error
      );

      return null;
    }
  };


  /* =========================
     VALIDAR PEDIDO QR
  ========================== */

  const isQrOrder = (
    order,
    qrConfig
  ) => {
    const selectedMaterial =
      getSelectedMaterial();


    /*
     * Primero se valida el material
     * almacenado dentro del pedido.
     */

    const orderProductCode =
      normalizeText(
        order
          ?.material
          ?.productCode
      );


    if (
      orderProductCode ===
      QR_PRODUCT_CODE
    ) {
      return true;
    }


    /*
     * Después se valida el material
     * seleccionado originalmente.
     */

    const selectedProductCode =
      normalizeText(
        selectedMaterial
          ?.productCode
      );


    if (
      selectedProductCode ===
      QR_PRODUCT_CODE
    ) {
      return true;
    }


    /*
     * Compatibilidad por tipo de flujo.
     */

    const orderFlowType =
      normalizeText(
        order?.flowType ||
        order?.flow_type ||
        order?.type
      );


    if (
      orderFlowType ===
      "qr"
    ) {
      return true;
    }


    const qrFlowType =
      normalizeText(
        qrConfig?.flowType ||
        qrConfig?.flow_type ||
        qrConfig?.type
      );


    if (
      qrFlowType ===
      "qr"
    ) {
      return true;
    }


    /*
     * Respaldo por configuración QR.
     */

    const destinationUrl =
      qrConfig?.destinationUrl ||
      qrConfig?.url ||
      "";


    const qrPosition =
      qrConfig?.position ||
      qrConfig?.posicion ||
      "";


    return Boolean(
      destinationUrl &&
      qrPosition
    );
  };


  /* =========================
     FORMATEAR MONEDA
  ========================== */

  const formatCurrency = (
    value
  ) => {
    const amount =
      Number(
        value
      );


    return Number.isFinite(
      amount
    )
      ? currencyFormatter.format(
          amount
        )
      : "—";
  };


  /* =========================
     FORMATEAR CANTIDAD
  ========================== */

  const formatQuantity = (
    value
  ) => {
    const quantity =
      Number(
        value
      );


    return Number.isFinite(
      quantity
    )
      ? quantityFormatter.format(
          quantity
        )
      : "—";
  };


  /* =========================
     OBTENER IMAGEN
  ========================== */

  const getLocalDesignFile = (
    key
  ) => {
    return new Promise(
      (resolve) => {
        if (
          !key ||
          !window.indexedDB
        ) {
          resolve(null);
          return;
        }

        const openRequest =
          indexedDB.open(
            DESIGN_FILE_DB,
            1
          );

        openRequest.onerror = () =>
          resolve(null);

        openRequest.onsuccess = () => {
          const database =
            openRequest.result;

          if (
            !database.objectStoreNames
              .contains(DESIGN_FILE_STORE)
          ) {
            database.close();
            resolve(null);
            return;
          }

          const request = database
            .transaction(
              DESIGN_FILE_STORE,
              "readonly"
            )
            .objectStore(DESIGN_FILE_STORE)
            .get(key);

          request.onsuccess = () => {
            database.close();
            resolve(
              request.result || null
            );
          };

          request.onerror = () => {
            database.close();
            resolve(null);
          };
        };
      }
    );
  };


  const resolveOrderImage = async (
    order
  ) => {
    const design = order?.design;

    if (
      design &&
      design.uploadLater !== true
    ) {
      const designUrl =
        design.fileUrl ||
        design.previewUrl ||
        design.url ||
        "";

      if (designUrl) {
        return {
          url: designUrl,
          isLocal: false
        };
      }

      const localFile =
        await getLocalDesignFile(
          design.localPreviewKey
        );

      if (
        localFile &&
        String(localFile.type || "")
          .startsWith("image/")
      ) {
        return {
          url: URL.createObjectURL(
            localFile
          ),
          isLocal: true
        };
      }
    }

    return (
      {
        url:
          order
            ?.material
            ?.imageUrl ||
          "",
        isLocal: false
      }
    );
  };


  /* =========================
     OBTENER NOMBRE
     DEL DISEÑO
  ========================== */

  const resolveDesignName = (
    order
  ) => {
    const design =
      order?.design;


    if (
      !design
    ) {
      return "—";
    }


    if (
      design.uploadLater ===
      true
    ) {
      return "Lo subiré más tarde";
    }


    return (
      design.fileName ||
      design.filename ||
      design.name ||
      "—"
    );
  };


  /* =========================
     OBTENER POSICIÓN DEL QR
  ========================== */

  const resolveQrPosition = (
    order,
    qrConfig
  ) => {
    return (
      order
        ?.qr
        ?.position ||

      order
        ?.qr
        ?.posicion ||

      order
        ?.qrConfig
        ?.position ||

      order
        ?.qrConfig
        ?.posicion ||

      order
        ?.qrConfiguration
        ?.position ||

      order
        ?.qrConfiguration
        ?.posicion ||

      qrConfig
        ?.position ||

      qrConfig
        ?.posicion ||

      ""
    );
  };


  /* =========================
     FORMATEAR POSICIÓN QR
  ========================== */

  const formatQrPosition = (
    value
  ) => {
    const normalizedValue =
      normalizeText(
        value
      );


    const positionLabels = {
      arriba: "Arriba",
      top: "Arriba",

      abajo: "Abajo",
      bottom: "Abajo",

      izquierda: "Izquierda",
      left: "Izquierda",

      derecha: "Derecha",
      right: "Derecha"
    };


    if (
      positionLabels[
        normalizedValue
      ]
    ) {
      return positionLabels[
        normalizedValue
      ];
    }


    if (
      !normalizedValue
    ) {
      return "—";
    }


    return (
      normalizedValue
        .charAt(0)
        .toUpperCase() +
      normalizedValue.slice(1)
    );
  };


  /* =========================
     OBTENER URL DESTINO
  ========================== */

  const resolveDestinationUrl = (
    order,
    qrConfig
  ) => {
    const destinationUrl =
      order
        ?.qr
        ?.destinationUrl ||

      order
        ?.qr
        ?.link ||

      order
        ?.qr
        ?.url ||

      order
        ?.qrConfig
        ?.destinationUrl ||

      order
        ?.qrConfig
        ?.link ||

      order
        ?.qrConfig
        ?.url ||

      order
        ?.qrConfiguration
        ?.destinationUrl ||

      order
        ?.qrConfiguration
        ?.link ||

      order
        ?.qrConfiguration
        ?.url ||

      qrConfig
        ?.destinationUrl ||

      qrConfig
        ?.link ||

      qrConfig
        ?.url ||

      "";


    return String(
      destinationUrl || ""
    ).trim();
  };


  /* =========================
     OBTENER TOTAL
  ========================== */

  const resolveTotal = (
    order
  ) => {
    const finalTotal =
      order
        ?.quote
        ?.finalTotal;


    const subtotal =
      order
        ?.quote
        ?.subtotal;


    if (
      Number.isFinite(
        Number(
          finalTotal
        )
      )
    ) {
      return finalTotal;
    }


    if (
      Number.isFinite(
        Number(
          subtotal
        )
      )
    ) {
      return subtotal;
    }


    return null;
  };


  /* =========================
     LIMPIAR IMAGEN
  ========================== */

  const clearImage = (
    imageElement
  ) => {
    if (
      !imageElement
    ) {
      return;
    }


    imageElement.hidden =
      true;


    imageElement.alt =
      "";


    imageElement.removeAttribute(
      "src"
    );
  };


  /* =========================
     OCULTAR POSICIÓN DEL QR
  ========================== */

  const hideQrPosition = (
    summary
  ) => {
    const row =
      summary.querySelector(
        "[data-order-summary-qr-position-row]"
      );


    const element =
      summary.querySelector(
        "[data-order-summary-qr-position]"
      );


    if (
      row
    ) {
      row.hidden =
        true;
    }


    if (
      element
    ) {
      element.textContent =
        "—";
    }
  };


  /* =========================
     OCULTAR URL DESTINO
  ========================== */

  const hideDestinationUrl = (
    summary
  ) => {
    const row =
      summary.querySelector(
        "[data-order-summary-destination-url-row]"
      );


    const element =
      summary.querySelector(
        "[data-order-summary-destination-url]"
      );


    if (
      row
    ) {
      row.hidden =
        true;
    }


    if (
      element
    ) {
      element.textContent =
        "—";


      element.removeAttribute(
        "title"
      );
    }
  };


  /* =========================
     LIMPIAR RESUMEN
  ========================== */

  const clearSummary = (
    summary
  ) => {
    if (
      !summary
    ) {
      return;
    }


    const valueElements =
      summary.querySelectorAll(
        [
          "[data-order-summary-material]",
          "[data-order-summary-quantity]",
          "[data-order-summary-qr-position]",
          "[data-order-summary-destination-url]",
          "[data-order-summary-unit-price]",
          "[data-order-summary-design]",
          "[data-order-summary-total]"
        ].join(",")
      );


    valueElements.forEach(
      (
        element
      ) => {
        element.textContent =
          "—";
      }
    );


    hideQrPosition(
      summary
    );


    hideDestinationUrl(
      summary
    );


    const imageElement =
      summary.querySelector(
        "[data-order-summary-image]"
      );


    clearImage(
      imageElement
    );
  };


  /* =========================
     RENDERIZAR IMAGEN
  ========================== */

  const renderImage = async (
    imageElement,
    order
  ) => {
    if (
      !imageElement
    ) {
      return;
    }


    const renderVersion =
      (imageRenderVersions.get(
        imageElement
      ) || 0) + 1;

    imageRenderVersions.set(
      imageElement,
      renderVersion
    );

    const resolvedImage =
      await resolveOrderImage(
        order
      );

    if (
      imageRenderVersions.get(
        imageElement
      ) !== renderVersion
    ) {
      if (resolvedImage.isLocal) {
        URL.revokeObjectURL(
          resolvedImage.url
        );
      }
      return;
    }

    const previousLocalUrl =
      localPreviewUrls.get(
        imageElement
      );

    if (previousLocalUrl) {
      URL.revokeObjectURL(
        previousLocalUrl
      );
      localPreviewUrls.delete(
        imageElement
      );
    }

    const imageUrl =
      resolvedImage.url;


    if (
      !imageUrl
    ) {
      clearImage(
        imageElement
      );


      return;
    }


    imageElement.src =
      imageUrl;

    if (resolvedImage.isLocal) {
      localPreviewUrls.set(
        imageElement,
        imageUrl
      );
    }


    imageElement.alt =
      order?.design?.fileName
        ? `Vista previa de ${order.design.fileName}`
        : order?.material?.name
          ? `Pedido en ${order.material.name}`
          : "Vista previa del pedido";


    imageElement.hidden =
      false;
  };


  /* =========================
     RENDERIZAR MATERIAL
  ========================== */

  const renderMaterial = (
    summary,
    order
  ) => {
    const element =
      summary.querySelector(
        "[data-order-summary-material]"
      );


    if (
      !element
    ) {
      return;
    }


    element.textContent =
      order
        ?.material
        ?.name ||
      "—";
  };


  /* =========================
     RENDERIZAR CANTIDAD
  ========================== */

  const renderQuantity = (
    summary,
    order
  ) => {
    const element =
      summary.querySelector(
        "[data-order-summary-quantity]"
      );


    if (
      !element
    ) {
      return;
    }


    const quantity =
      Number(
        order
          ?.quote
          ?.quantity
      );


    element.textContent =
      Number.isFinite(
        quantity
      )
        ? `${formatQuantity(
            quantity
          )} pzas`
        : "—";
  };


  /* =========================
     RENDERIZAR POSICIÓN QR
  ========================== */

  const renderQrPosition = (
    summary,
    order
  ) => {
    const row =
      summary.querySelector(
        "[data-order-summary-qr-position-row]"
      );


    const element =
      summary.querySelector(
        "[data-order-summary-qr-position]"
      );


    if (
      !row ||
      !element
    ) {
      return;
    }


    const qrConfig =
      getQrConfig();


    const qrOrder =
      isQrOrder(
        order,
        qrConfig
      );


    if (
      !qrOrder
    ) {
      hideQrPosition(
        summary
      );


      return;
    }


    const position =
      resolveQrPosition(
        order,
        qrConfig
      );


    row.hidden =
      false;


    element.textContent =
      formatQrPosition(
        position
      );
  };


  /* =========================
     RENDERIZAR URL DESTINO
  ========================== */

  const renderDestinationUrl = (
    summary,
    order
  ) => {
    const row =
      summary.querySelector(
        "[data-order-summary-destination-url-row]"
      );


    const element =
      summary.querySelector(
        "[data-order-summary-destination-url]"
      );


    if (
      !row ||
      !element
    ) {
      return;
    }


    const qrConfig =
      getQrConfig();


    const qrOrder =
      isQrOrder(
        order,
        qrConfig
      );


    if (
      !qrOrder
    ) {
      hideDestinationUrl(
        summary
      );


      return;
    }


    const destinationUrl =
      resolveDestinationUrl(
        order,
        qrConfig
      );


    row.hidden =
      false;


    element.textContent =
      destinationUrl ||
      "—";


    if (
      destinationUrl
    ) {
      element.title =
        destinationUrl;

    } else {
      element.removeAttribute(
        "title"
      );
    }
  };


  /* =========================
     RENDERIZAR PRECIO
     UNITARIO
  ========================== */

  const renderUnitPrice = (
    summary,
    order
  ) => {
    const element =
      summary.querySelector(
        "[data-order-summary-unit-price]"
      );


    if (
      !element
    ) {
      return;
    }


    element.textContent =
      formatCurrency(
        order
          ?.quote
          ?.unitPrice
      );
  };


  /* =========================
     RENDERIZAR DISEÑO
  ========================== */

  const renderDesign = (
    summary,
    order
  ) => {
    const element =
      summary.querySelector(
        "[data-order-summary-design]"
      );


    if (
      !element
    ) {
      return;
    }


    element.textContent =
      resolveDesignName(
        order
      );
  };


  /* =========================
     RENDERIZAR TOTAL
  ========================== */

  const renderTotal = (
    summary,
    order
  ) => {
    const element =
      summary.querySelector(
        "[data-order-summary-total]"
      );


    if (
      !element
    ) {
      return;
    }


    element.textContent =
      formatCurrency(
        resolveTotal(
          order
        )
      );
  };


  const renderPaymentTotals = async (
    summary,
    order,
    suppliedCart = null
  ) => {
    const discountRow =
      summary.querySelector(
        "[data-order-summary-discount-row]"
      );

    const discountLabel =
      summary.querySelector(
        "[data-order-summary-discount-label]"
      );

    const discountValue =
      summary.querySelector(
        "[data-order-summary-discount]"
      );

    const itemTotal =
      resolveTotal(order);

    let payTotal = itemTotal;
    let discountAmount = 0;
    let couponCodes = [];

    try {
      const cart = suppliedCart ||
        await (
          await waitForPeguuCart()
        ).getCurrentCart();

      const pricing =
        cart?.pricing || {};

      discountAmount =
        Number(
          pricing.discountAmount
        ) || 0;

      const productsSubtotal =
        Number(pricing.subtotal);
      const pricingTotal =
        pricing.total == null
          ? Number.NaN
          : Number(pricing.total);
      const shippingCost =
        Number(pricing.shippingCost) || 0;

      couponCodes =
        (Array.isArray(cart?.coupons)
          ? cart.coupons
          : cart?.coupon
            ? [cart.coupon]
            : [])
          .map((coupon) =>
            String(coupon?.code || "").trim()
          )
          .filter(Boolean);

      if (Number.isFinite(pricingTotal)) {
        payTotal = Math.max(0, pricingTotal);
      } else if (Number.isFinite(productsSubtotal)) {
        payTotal = Math.max(
          0,
          productsSubtotal -
            discountAmount +
            shippingCost
        );
      } else if (
        Number.isFinite(Number(itemTotal))
      ) {
        payTotal = Math.max(
          0,
          Number(itemTotal) -
            discountAmount +
            shippingCost
        );
      }
    } catch (error) {
      console.warn(
        "Peguu Order Summary: no fue posible actualizar el total con cupón.",
        error
      );
    }

    if (discountRow) {
      discountRow.hidden =
        discountAmount <= 0;
    }

    if (discountLabel) {
      discountLabel.textContent =
        couponCodes.length
          ? `Descuento (${couponCodes.join(", ")})`
          : "Descuento";
    }

    if (discountValue) {
      discountValue.textContent =
        discountAmount > 0
          ? `−${formatCurrency(discountAmount)}`
          : "—";
    }

    const totalElement =
      summary.querySelector(
        "[data-order-summary-total]"
      );

    if (totalElement) {
      totalElement.textContent =
        formatCurrency(payTotal);
    }
  };


  /* =========================
     RENDERIZAR RESUMEN
  ========================== */

  const renderSummary = async (
    summary
  ) => {
    if (
      !summary
    ) {
      return;
    }


    let order =
      getOrder();


    if (
      !order
    ) {
      order =
        await getSingleCartOrder(
          summary
        );
    }


    if (
      !order
    ) {
      clearSummary(
        summary
      );


      return;
    }


    /* MATERIAL */

    renderMaterial(
      summary,
      order
    );


    /* CANTIDAD */

    renderQuantity(
      summary,
      order
    );


    /* POSICIÓN DEL QR */

    renderQrPosition(
      summary,
      order
    );


    /* URL DESTINO */

    renderDestinationUrl(
      summary,
      order
    );


    /* PRECIO UNITARIO */

    renderUnitPrice(
      summary,
      order
    );


    /* DISEÑO */

    renderDesign(
      summary,
      order
    );


    /* PRECIO TOTAL */

    if (
      summary.dataset
        .orderSummaryShowCoupon ===
        "true"
    ) {
      await renderPaymentTotals(
        summary,
        order
      );
    } else {
      renderTotal(
        summary,
        order
      );
    }


    /* IMAGEN */

    const imageElement =
      summary.querySelector(
        "[data-order-summary-image]"
      );


    renderImage(
      imageElement,
      order
    );
  };


  /* =========================
     RENDERIZAR TODOS
  ========================== */

  const renderAllSummaries =
    () => {
      document
        .querySelectorAll(
          "[data-peguu-order-summary]"
        )
        .forEach(
          renderSummary
        );
    };


  /* =========================
     INICIALIZACIÓN
  ========================== */

  const initialize =
    () => {
      renderAllSummaries();
    };


  /* =========================
     DOM READY
  ========================== */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialize
    );

  } else {
    initialize();
  }


  /* =========================
     PEDIDO ACTUALIZADO
  ========================== */

  document.addEventListener(
    ORDER_UPDATED_EVENT,
    renderAllSummaries
  );


  /* =========================
     CARRITO ACTUALIZADO
  ========================== */

  document.addEventListener(
    "peguu:cart:ready",
    renderAllSummaries
  );

  document.addEventListener(
    "peguu:cart:updated",
    (event) => {
      const action = String(event.detail?.action || "");

      if (action === "coupon" || action === "coupon-remove") {
        document
          .querySelectorAll('[data-peguu-order-summary][data-order-summary-show-coupon="true"]')
          .forEach(async (summary) => {
            const order = getOrder() || await getSingleCartOrder(summary);

            if (order) {
              await renderPaymentTotals(summary, order, event.detail?.cart);
            }
          });
        return;
      }

      renderAllSummaries();
    }
  );

  document.addEventListener(
    "peguu:cart:merged",
    renderAllSummaries
  );


  /* =========================
     CONFIGURACIÓN QR
     ACTUALIZADA
  ========================== */

  document.addEventListener(
    QR_CONFIG_UPDATED_EVENT,
    renderAllSummaries
  );


  /* =========================
     SHOPIFY THEME EDITOR
  ========================== */

  document.addEventListener(
    "shopify:section:load",
    renderAllSummaries
  );

})();
